import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/** @type {WeakMap<HTMLElement, Promise<boolean>>} */
const pendingHydrations = new WeakMap();

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return Promise.resolve(false);
  }

  const pending = pendingHydrations.get(section);
  if (pending) return pending;

  // This is optional enrichment of an already usable server-rendered section.
  // Keep its markup on failure and never mark an aborted render as hydrated.
  const current = () => section.isConnected && document.getElementById(section.id) === section;
  section.dataset.hydrationState = 'pending';
  const task = (async () => {
    try {
      const html = await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });
      if (!current()) return false;
      if (!html) {
        section.dataset.hydrationState = 'idle';
        return false;
      }
      section.dataset.hydrated = 'true';
      section.dataset.hydrationState = 'ready';
      return true;
    } catch {
      // SectionRenderer logs the original failure. Expose a recoverable state
      // without an unhandled rejection, a forced navigation or a retry loop.
      if (current()) section.dataset.hydrationState = 'error';
      return false;
    } finally {
      pendingHydrations.delete(section);
    }
  })();
  pendingHydrations.set(section, task);
  return task;
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  return new Promise((resolve) => {
    onDocumentReady(() => {
      requestIdleCallback(() => resolve(hydrateSection(sectionId, url)));
    });
  });
}
